import { useEffect } from 'react';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import adobeTags from 'src/types/adobe-tags';
import UrlHelper from 'src/utils/url.helper';

export interface DigitalData {
    event?: EventInfo[];
    page?: {
        pageInfo: {
            country: string;
            language: string;
            pageName: string;
        };
        category: {
            primaryCategory: string;
        };
    };
}

interface EventInfo {
    eventInfo: {
        eventAction: string;
        eventAsset: string;
        eventType?: string;
    };
}

export interface TrackedErrors {
    error: string;
    id: string;
}

function getPageName(currentPage: string): string {
    let pageName: string;
    switch (currentPage) {
        case BudgetPages.PERSONALIZE:
            pageName = adobeTags.Page.Personalize;
            break;
        case BudgetPages.HOME:
            pageName = adobeTags.Page.Home;
            break;
        case BudgetPages.SETTINGS:
            pageName = adobeTags.Page.Settings;
            break;
        case BudgetPages.HISTORY:
            pageName = adobeTags.Page.History;
            break;
        case BudgetPages.TRANSACTIONS:
            pageName = adobeTags.Page.Transactions;
            break;
        case BudgetPages.TRANSACTIONS_REVIEW:
            pageName = adobeTags.Page.ReviewTransactions;
            break;
        case BudgetPages.TRANSACTIONS_RULES:
            pageName = adobeTags.Page.TransactionsRules;
            break;
        case BudgetPages.INELIGIBLE:
            pageName = adobeTags.Page.Ineligible;
            break;
        case UrlHelper.getFullPath('error'):
            pageName = adobeTags.Page.Error;
            break;
        default:
            if (currentPage.includes('/spendsmart/transactions/')) {
                pageName = adobeTags.Page.TransactionsDetails;
            } else {
                console.error(
                    'Adobe Tracking error: page could not be identified, please add page name to getPageName' +
                        ' function.'
                );
                return null;
            }
            break;
    }
    return pageName;
}

export function useTrackPage(pageName: string): void {
    useEffect(() => {
        if (typeof window.digitalData === 'undefined') {
            window.digitalData = {};
        }
        window.digitalData.page = {
            pageInfo: {
                country: 'us',
                language: 'english',
                pageName
            },
            category: {
                primaryCategory: 'axp-spend-smart'
            }
        };
        try {
            window._satellite?.track('page');
        } catch (error) {
            console.error('Adobe DMT Error: ', error);
        }
    }, []);
}

export function trackError(errorName: string, callOnlyOnce?: boolean): void {
    if (window.digitalData) {
        if (callOnlyOnce && window.digitalData.event[0].eventInfo.eventAsset === errorName) return;
        window.digitalData.event = [];
        window.digitalData.event.push({
            eventInfo: {
                eventAction: 'error',
                eventAsset: errorName,
                eventType: 'pageInteraction'
            }
        });
        try {
            window._satellite?.track('event');
        } catch (error) {
            console.error('Adobe DMT Error: ', error);
        }
    }
}

export function trackClick(currentPage: string, buttonName: string, widgetName?: string): void {
    const pageName = getPageName(currentPage);
    if (!pageName) return;

    if (window.digitalData) {
        window.digitalData.event = [];
        window.digitalData.event.push({
            eventInfo: {
                eventAction: 'click',
                eventAsset: (widgetName ? widgetName + '-' : '') + (pageName + '-' + buttonName),
                eventType: 'pageInteraction'
            }
        });
        try {
            window._satellite?.track('event');
        } catch (error) {
            console.error('Adobe DMT Error: ', error);
        }
    }
}

export function trackImpression(currentPage: string, impressionEvent: string, widgetName?: string): void {
    const pageName = getPageName(currentPage);
    if (!pageName) return;

    if (window.digitalData) {
        window.digitalData.event = [];
        window.digitalData.event.push({
            eventInfo: {
                eventAction: 'impression',
                eventAsset: (widgetName ? widgetName + '-' : '') + (pageName + '-' + impressionEvent),
                eventType: 'pageInteraction'
            }
        });
        try {
            window._satellite?.track('event');
        } catch (error) {
            console.error('Adobe DMT Error: ', error);
        }
    }
}

export function trackClickWithoutPath(buttonName: string): void {
    if (window.digitalData) {
        window.digitalData.event = [];
        window.digitalData.event.push({
            eventInfo: {
                eventAction: 'click',
                eventAsset: buttonName,
                eventType: 'pageInteraction'
            }
        });
        try {
            window._satellite?.track('event');
        } catch (error) {
            console.error('Adobe DMT Error: ', error);
        }
    }
}
